import { useEffect, useState } from "react";
import { Plus, Archive, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  archiveProject,
  createProject,
  listAllProjects,
  type ProjectSummary,
} from "@/lib/api";

export function AdminProjectsPage() {
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function refresh() {
    try {
      setProjects(await listAllProjects());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleArchive(id: string) {
    if (!confirm(`Archive project '${id}'? Tasks tagged with it will be hidden.`))
      return;
    await archiveProject(id);
    await refresh();
  }

  return (
    <div className="container py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Projects</h1>
          <p className="text-sm text-muted-foreground">
            Project namespaces. Each task is tagged with one. Forks edit
            these to match their organization.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" /> New project
            </Button>
          </DialogTrigger>
          <NewProjectDialog
            onCreated={async () => {
              setOpen(false);
              await refresh();
            }}
          />
        </Dialog>
      </div>

      {error && (
        <div className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {!projects && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {projects && (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Project</th>
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-medium">
                      <span style={{ color: p.color }} aria-hidden>
                        {p.icon ?? "•"}
                      </span>
                      {p.name}
                    </div>
                    {p.description && (
                      <div className="text-xs text-muted-foreground">
                        {p.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{p.id}</td>
                  <td className="px-4 py-3">
                    {p.archived_at ? (
                      <Badge variant="outline">archived</Badge>
                    ) : (
                      <Badge variant="success">active</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!p.archived_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleArchive(p.id)}
                      >
                        <Archive className="mr-2 h-3.5 w-3.5" /> Archive
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewProjectDialog({ onCreated }: { onCreated: () => void | Promise<void> }) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState("#64748B");
  const [icon, setIcon] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await createProject({
        id,
        name,
        description: description || undefined,
        color,
        icon: icon || undefined,
      });
      await onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>New project</DialogTitle>
        <DialogDescription>
          Tasks tagged with this project ID will appear in this namespace.
        </DialogDescription>
      </DialogHeader>
      <form className="space-y-4" onSubmit={submit}>
        <div className="space-y-1.5">
          <Label htmlFor="id">ID</Label>
          <Input
            id="id"
            placeholder="acme"
            value={id}
            onChange={(e) => setId(e.target.value)}
            pattern="[a-z0-9-]+"
            required
          />
          <p className="text-xs text-muted-foreground">
            Lowercase letters, digits, dashes. Cannot be changed later.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="name">Display name</Label>
          <Input
            id="name"
            placeholder="Acme SaaS"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="desc">Description</Label>
          <Input
            id="desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="color">Color</Label>
            <Input
              id="color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="h-9 p-1"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="icon">Icon (emoji)</Label>
            <Input
              id="icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="🛠️"
              maxLength={4}
            />
          </div>
        </div>
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full">
          {busy ? "Creating…" : "Create project"}
        </Button>
      </form>
    </DialogContent>
  );
}
